// skills/bounty/bountyUI.js
// Bounty UI rendering and interaction

import { bountySkill } from './bounty.js';
import { PLAYER_CONTRACTS, CONTRACT_TIERS, BOUNTY_CONFIG, getBountyXP, getBountyMark } from './bountyData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS, GAME_CONFIG } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { formatNumber, formatWithCommas } from '../../utils/format.js';
import { getItemName, getItemIcon } from '../../data/items/index.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { getXPPerHour } from '../../systems/experience.js';
import { skillRegistry, getSkillIcon } from '../../core/skillRegistry.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';

// Current tab state
let currentTab = 'tier1';
let lastKnownLevel = 0;

/**
 * Seeded random number generator for consistent contract rotation
 * Based on day of year + tier, so contracts rotate daily per tier
 */
function seededRandom(seed) {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
}

/**
 * Shuffle array with seed for deterministic rotation
 */
function seededShuffle(array, seed) {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
        const j = Math.floor(seededRandom(seed + i) * (i + 1));
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

/**
 * Get rotation seed based on current day and tier
 * Contracts rotate daily within each tier
 */
function getRotationSeed(tier) {
    const now = new Date();
    const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    return dayOfYear * 100 + tier;
}

/**
 * Render compact skill header (no tools for Bounty)
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.BOUNTY);
    const xpPerHour = getXPPerHour(SKILLS.BOUNTY);
    const activeContracts = bountySkill.getActiveContracts();

    // Calculate display values
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let levelDisplay, progressPercent, progressText;

    if (isMax && virtualEnabled) {
        levelDisplay = `${skill.level} (${skill.virtualLevel})`;
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        levelDisplay = `${skill.level} (Max)`;
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        levelDisplay = skill.level;
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.BOUNTY)}</span>
                    <span class="compact-name">Bounty</span>
                    ${renderSkillInfo(SKILLS.BOUNTY)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.BOUNTY)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-contracts">
                    <span class="contracts-label">Active:</span>
                    <span class="contracts-count">${activeContracts.length}/${BOUNTY_CONFIG.MAX_ACTIVE_CONTRACTS}</span>
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${Math.floor(progressPercent)}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Format item name for display
 */
function formatItemName(itemId) {
    const name = getItemName(itemId);
    if (name && name !== itemId) {
        return name;
    }
    // Fallback: format the ID
    return itemId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

/**
 * Get tier color for display
 */
function getTierColor(tier) {
    const colors = {
        1: '#4ade80', // green
        2: '#60a5fa', // blue
        3: '#c084fc', // purple
        4: '#f97316', // orange
        5: '#ef4444'  // red
    };
    return colors[tier] || '#9ca3af';
}

/**
 * Render a single contract card
 */
function renderContractCard(contract, isActive = false) {
    const activeContract = isActive ? bountySkill.getActiveContract(contract.id) : null;
    const completionPercent = isActive ? bountySkill.getContractCompletionPercent(contract.id) : 0;
    const canDeliver = isActive ? bountySkill.canDeliverContract(contract.id).success : false;
    const tierColor = getTierColor(contract.tier);

    // Use active contract data for requirements/rewards if available (has generated targets)
    const requirements = activeContract?.requirements || contract.requirements || [];
    const rewards = activeContract?.rewards || contract.rewards || contract.baseRewards || {};

    // Check if mega contracts talent is available (for showing mega requirements)
    const hasMegaTalent = bountySkill.hasMegaContracts();

    // Build requirements HTML
    const requirementsHtml = requirements.map(req => {
        if (req.itemId) {
            const itemName = formatItemName(req.itemId);
            const itemIcon = getItemIcon(req.itemId);
            if (isActive) {
                // Active contract: show items gathered since accepting
                const progress = bountySkill.getRequirementProgress(contract.id, req.itemId);
                const progressClass = progress.current >= progress.target ? 'complete' : '';
                // Cap displayed progress at target (no need to show 113/39)
                const displayCurrent = Math.min(progress.current, progress.target);
                return `
                    <div class="contract-requirement ${progressClass}" data-req-id="${req.itemId}">
                        <span class="req-name">${itemIcon} ${itemName}</span>
                        <span class="req-progress">${formatNumber(displayCurrent)} / ${formatNumber(progress.target)}</span>
                    </div>
                `;
            } else {
                // Board view: show target (and mega target if talent unlocked)
                const megaHtml = hasMegaTalent ? `<span class="mega-req" title="Mega contract requirement">(Mega: ${formatNumber(req.target * 5)})</span>` : '';
                return `
                    <div class="contract-requirement" data-req-id="${req.itemId}">
                        <span class="req-name">${itemIcon} ${itemName}</span>
                        <span class="req-progress">${formatNumber(req.target)} ${megaHtml}</span>
                    </div>
                `;
            }
        } else if (req.mobId) {
            const mobName = req.mobId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            if (isActive) {
                // Active contract: show kills progress
                const progress = bountySkill.getRequirementProgress(contract.id, req.mobId);
                const progressClass = progress.current >= progress.target ? 'complete' : '';
                // Cap displayed progress at target
                const displayCurrent = Math.min(progress.current, progress.target);
                return `
                    <div class="contract-requirement ${progressClass}" data-req-id="${req.mobId}">
                        <span class="req-name">⚔️ ${mobName}</span>
                        <span class="req-progress">${displayCurrent} / ${progress.target}</span>
                    </div>
                `;
            } else {
                // Board view: show target (and mega target if talent unlocked)
                const megaHtml = hasMegaTalent ? `<span class="mega-req" title="Mega contract requirement">(Mega: ${req.target * 5})</span>` : '';
                return `
                    <div class="contract-requirement" data-req-id="${req.mobId}">
                        <span class="req-name">⚔️ ${mobName}</span>
                        <span class="req-progress">${req.target} ${megaHtml}</span>
                    </div>
                `;
            }
        }
        return '';
    }).join('');

    // Check if this contract has item requirements (for "items consumed" note)
    const hasItemReqs = requirements.some(req => req.itemId);

    // Build rewards HTML - always use compact format to keep XP values together
    // Apply bounty mark multiplier for kill contracts
    const markMobId = contract.type === 'kill' ? (requirements[0]?.mobId) : null;
    const markMult = markMobId ? (1 + getBountyMark(markMobId).bountyBonus) : 1;
    const isBoosted = markMult > 1;
    const boostClass = isBoosted ? ' mark-boosted' : '';

    let rewardsHtml = '';
    const xpEntries = rewards.xp ? Object.entries(rewards.xp) : [];
    const bountyXP = getBountyXP(contract);

    if (rewards.gold) {
        const displayGold = isBoosted ? Math.floor(rewards.gold * markMult) : rewards.gold;
        rewardsHtml += `<span class="reward-gold${boostClass}">${getItemIcon('coins')}${formatNumber(displayGold)}</span>`;
    }

    // Each XP entry as its own reward span for consistent flex spacing
    for (const [skill, amount] of xpEntries) {
        const displayXP = isBoosted ? Math.floor(amount * markMult) : amount;
        rewardsHtml += `<span class="reward-xp${boostClass}">${getSkillIcon(skill)}${formatNumber(displayXP)}</span>`;
    }
    const displayBountyXP = isBoosted ? Math.floor(bountyXP * markMult) : bountyXP;
    rewardsHtml += `<span class="reward-xp${boostClass}">${getSkillIcon(SKILLS.BOUNTY)}${formatNumber(displayBountyXP)}</span>`;

    // Build consumed items display for deliver button (resource contracts only)
    const itemRequirements = requirements.filter(req => req.itemId);
    const consumedItemsHtml = itemRequirements.length > 0 ? itemRequirements.map(req => {
        const itemIcon = getItemIcon(req.itemId);
        return `<span class="consume-item">${itemIcon} -${formatNumber(req.target)}</span>`;
    }).join(' ') : '';

    // Determine action button
    let actionButton = '';
    if (isActive) {
        if (canDeliver) {
            actionButton = `
                <button class="contract-btn deliver" onclick="window.bountyUI.deliverContract('${contract.id}')">
                    Deliver ${consumedItemsHtml ? `<span class="consume-warning">(${consumedItemsHtml})</span>` : ''}
                </button>
            `;
        } else {
            actionButton = `
                <button class="contract-btn abandon" onclick="window.bountyUI.abandonContract('${contract.id}')">
                    Abandon
                </button>
            `;
        }
    } else {
        const canAccept = bountySkill.canAcceptContract(contract.id);
        const tierCost = CONTRACT_TIERS[contract.tier]?.cost || 0;
        const costHtml = tierCost > 0 ? `<span class="cost-warning">(<span class="cost-icon">${getItemIcon('coins')}</span> -${formatNumber(tierCost)})</span>` : '';
        const hasMegaTalent = bountySkill.hasMegaContracts();
        const megaCost = tierCost * 5;
        const megaCostHtml = megaCost > 0 ? `<span class="cost-warning">(<span class="cost-icon">${getItemIcon('coins')}</span> -${formatNumber(megaCost)})</span>` : '';

        if (canAccept.success) {
            actionButton = `
                <div class="contract-accept-buttons">
                    <button class="contract-btn accept" onclick="window.bountyUI.acceptContract('${contract.id}')">
                        Accept ${costHtml}
                    </button>
                    ${hasMegaTalent ? `
                        <button class="contract-btn accept-mega" onclick="window.bountyUI.acceptContract('${contract.id}', true)" title="5x requirements, 7x rewards">
                            Mega ${megaCostHtml}
                        </button>
                    ` : ''}
                </div>
            `;
        } else {
            actionButton = `
                <button class="contract-btn disabled" disabled title="${canAccept.reason}">
                    ${canAccept.reason}
                </button>
            `;
        }
    }

    // Build pilferer assist indicator (only for active contracts with assists)
    const pilfererAssists = activeContract?.pilfererAssists || 0;
    const pilfererAssistHtml = pilfererAssists > 0 ? `
        <span class="pilferer-assist-indicator" title="Pilferers assisted ${pilfererAssists} time${pilfererAssists !== 1 ? 's' : ''} - reduced requirements">
            🗡️ ${pilfererAssists}
        </span>
    ` : '';

    // Check if this is a mega contract
    const isMegaContract = activeContract?.isMega || false;
    const megaBadge = isMegaContract ? '<span class="mega-badge">MEGA</span>' : '';

    // Check if this contract is pinned (Dedicated Supplier)
    const isPinned = bountySkill.isPinned(contract.id);
    const hasPinTalent = bountySkill.hasDedicatedSupplier();
    const pinnedBadge = isPinned ? '<span class="pinned-badge" title="Pinned - always available">📌</span>' : '';

    // Pin button (only show on board view, not active contracts)
    let pinButton = '';
    if (hasPinTalent && !isActive) {
        if (isPinned) {
            pinButton = `<button class="pin-btn pinned" onclick="event.stopPropagation(); window.bountyUI.unpinContract()" title="Unpin contract">📌</button>`;
        } else {
            pinButton = `<button class="pin-btn" onclick="event.stopPropagation(); window.bountyUI.pinContract('${contract.id}')" title="Pin contract (always available)">📍</button>`;
        }
    }

    return `
        <div class="contract-card ${isActive ? 'active' : ''} ${canDeliver ? 'ready' : ''} ${isMegaContract ? 'mega' : ''} ${isPinned ? 'pinned' : ''}" data-contract="${contract.id}">
            <div class="contract-header">
                <div class="contract-title">
                    <span class="contract-tier" style="background-color: ${tierColor};">T${contract.tier}</span>
                    ${megaBadge}
                    ${pinnedBadge}
                    <span class="contract-name">${contract.name}</span>
                </div>
                ${pinButton}
                ${pilfererAssistHtml}
                <span class="contract-type ${contract.type}">${contract.type}</span>
            </div>
            <p class="contract-description">${contract.description}</p>

            <div class="contract-footer">
                <div class="contract-section requirements-section">
                    <h4>Requirements</h4>
                    ${hasItemReqs ? '<p class="consume-note">Items consumed on delivery</p>' : ''}
                    <div class="contract-requirements">
                        ${requirementsHtml}
                    </div>
                </div>

                ${isActive ? `
                    <div class="contract-progress-bar">
                        <div class="contract-progress-fill" style="width: ${completionPercent}%;"></div>
                    </div>
                    <div class="contract-progress-text">${Math.floor(completionPercent)}% Complete</div>
                ` : ''}

                <div class="contract-section rewards-section">
                    <h4>Rewards</h4>
                    <div class="contract-rewards">
                        ${rewardsHtml}
                    </div>
                </div>

                <div class="contract-actions">
                    ${actionButton}
                </div>
            </div>
        </div>
    `;
}

/**
 * Render a specific tier's contracts
 */
function renderTierTab(tierNum) {
    const skill = gameState.getSkill(SKILLS.BOUNTY);
    const tierConfig = CONTRACT_TIERS[tierNum];
    const tierColor = getTierColor(tierNum);
    const isUnlocked = skill.level >= tierConfig.bountyLevel;
    const activeContracts = bountySkill.getActiveContracts();
    const activeCount = activeContracts.length;
    const maxActive = BOUNTY_CONFIG.MAX_ACTIVE_CONTRACTS;

    if (!isUnlocked) {
        // Locked tier - show preview
        const levelsNeeded = tierConfig.bountyLevel - skill.level;
        return `
            <div class="bounty-section">
                <div class="section-header">
                    <h3 class="section-title">Tier ${tierNum} Contracts</h3>
                    <span class="active-count">Active: ${activeCount}/${maxActive}</span>
                </div>
                <div class="locked-tier-info large">
                    <div class="locked-icon">🔒</div>
                    <h4>Unlocks at Bounty Level ${tierConfig.bountyLevel}</h4>
                    <p class="locked-message">${levelsNeeded} level${levelsNeeded !== 1 ? 's' : ''} to go!</p>
                    <div class="locked-rewards-preview">
                        <div class="preview-card">
                            <span class="preview-label">${getItemIcon('coins')} Gold Rewards</span>
                            <span class="preview-value gold">${formatNumber(tierConfig.goldReward[0])} - ${formatNumber(tierConfig.goldReward[1])}</span>
                        </div>
                        <div class="preview-card">
                            <span class="preview-label">XP Multiplier</span>
                            <span class="preview-value">⭐ ${tierConfig.xpMultiplier}x</span>
                        </div>
                        <div class="preview-card">
                            <span class="preview-label">Skill Range</span>
                            <span class="preview-value">📊 Level ${tierConfig.skillLevel[0]}-${tierConfig.skillLevel[1]}</span>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    // Get contracts for this tier
    const availableContracts = bountySkill.getAvailableContracts();
    const allTierContracts = availableContracts
        .filter(c => c.tier === tierNum && !bountySkill.isContractActive(c.id));

    // Apply daily rotation and display limit
    const rotationSeed = getRotationSeed(tierNum);
    const shuffledContracts = seededShuffle(allTierContracts, rotationSeed);
    const displayLimit = BOUNTY_CONFIG.CONTRACTS_DISPLAYED_PER_TIER || 12;
    const tierContracts = shuffledContracts.slice(0, displayLimit);

    const contractCards = tierContracts.map(c => renderContractCard(c, false)).join('');

    return `
        <div class="bounty-section">
            <div class="section-header">
                <h3 class="section-title">Tier ${tierNum} Contracts</h3>
                <span class="active-count">Active: ${activeCount}/${maxActive}</span>
            </div>
            <p class="section-desc">
                ${tierNum === 1 ? 'Entry-level contracts for new bounty hunters.' : ''}
                ${tierNum === 2 ? 'Intermediate contracts with better rewards.' : ''}
                ${tierNum === 3 ? 'Challenging contracts for experienced hunters.' : ''}
                ${tierNum === 4 ? 'High-tier contracts requiring significant resources.' : ''}
                ${tierNum === 5 ? 'Elite contracts with the best rewards in the realm.' : ''}
            </p>
            <div class="contracts-grid">
                ${contractCards || '<p class="no-contracts">No contracts available. Check back later!</p>'}
            </div>
        </div>
    `;
}

/**
 * Render the active contracts tab
 */
function renderActiveTab() {
    const activeContracts = bountySkill.getActiveContracts();

    if (activeContracts.length === 0) {
        return `
            <div class="bounty-section active-section">
                <h3 class="section-title">Active Contracts</h3>
                <div class="empty-state">
                    <p>You have no active contracts.</p>
                    <p>Visit the Contract Board to accept new contracts.</p>
                    <button class="action-button" onclick="window.bountyUI.switchTab('tier1')">
                        View Contracts
                    </button>
                </div>
            </div>
        `;
    }

    const contractCards = activeContracts.map(active => {
        const contract = bountySkill.getContractDefinition(active.contractId);
        if (!contract) return '';
        return renderContractCard(contract, true);
    }).join('');

    return `
        <div class="bounty-section active-section">
            <h3 class="section-title">Active Contracts</h3>
            <p class="section-desc">Complete the requirements and deliver to claim your rewards.</p>
            <div class="contracts-grid active-grid">
                ${contractCards}
            </div>
        </div>
    `;
}

/**
 * Render compact active contracts summary (shows on both tabs)
 */
function renderActiveContractsSummary() {
    const activeContracts = bountySkill.getActiveContracts();
    if (activeContracts.length === 0) return '';

    const summaryItems = activeContracts.map(active => {
        const contract = bountySkill.getContractDefinition(active.contractId);
        if (!contract) return '';

        const completionPercent = bountySkill.getContractCompletionPercent(active.contractId);
        const canDeliver = bountySkill.canDeliverContract(active.contractId).success;
        const tierColor = getTierColor(active.tier || contract.tier);

        return `
            <div class="active-contract-mini ${canDeliver ? 'ready' : ''}">
                <span class="mini-tier" style="background-color: ${tierColor};">T${active.tier || contract.tier}</span>
                <span class="mini-name">${contract.name}</span>
                <div class="mini-progress-bar">
                    <div class="mini-progress-fill ${canDeliver ? 'complete' : ''}" style="width: ${completionPercent}%;"></div>
                </div>
                <span class="mini-percent">${Math.floor(completionPercent)}%</span>
                ${canDeliver ? `
                    <button class="mini-deliver-btn" onclick="window.bountyUI.deliverContract('${active.contractId}')">
                        Deliver
                    </button>
                ` : ''}
            </div>
        `;
    }).join('');

    return `
        <div class="active-contracts-summary">
            <h4 class="summary-title">Active Contracts (${activeContracts.length}/${BOUNTY_CONFIG.MAX_ACTIVE_CONTRACTS})</h4>
            <div class="active-contracts-list">
                ${summaryItems}
            </div>
        </div>
    `;
}

/**
 * Render tab navigation
 */
function renderTabs() {
    const skill = gameState.getSkill(SKILLS.BOUNTY);
    const activeCount = bountySkill.getActiveContracts().length;

    // Build tier tabs
    const tierTabs = Object.entries(CONTRACT_TIERS).map(([tierNum, tierConfig]) => {
        const tier = parseInt(tierNum);
        const isUnlocked = skill.level >= tierConfig.bountyLevel;
        const isActive = currentTab === `tier${tier}`;
        const tierColor = getTierColor(tier);

        return `
            <button class="skill-tab ${isActive ? 'active' : ''} ${!isUnlocked ? 'locked' : ''}"
                    onclick="window.bountyUI.switchTab('tier${tier}')"
                    style="${isActive ? `border-color: ${tierColor}; color: ${tierColor};` : ''}"
                    title="${!isUnlocked ? `Unlocks at level ${tierConfig.bountyLevel}` : `Tier ${tier} Contracts`}">
                ${!isUnlocked ? '🔒 ' : ''}T${tier}
            </button>
        `;
    }).join('');

    return `
        <div class="skill-tabs">
            ${tierTabs}
            <button class="skill-tab ${currentTab === 'active' ? 'active' : ''}"
                    onclick="window.bountyUI.switchTab('active')">
                Active ${activeCount > 0 ? `(${activeCount})` : ''}
            </button>
        </div>
    `;
}

/**
 * Main render function
 */
export function renderBountyUI(params = {}) {
    // Only override currentTab if an actual tier/active tab is specified
    // Don't override with generic 'contracts' tab - preserve the user's tier selection
    if (params.tab && params.tab !== 'contracts') {
        currentTab = params.tab;
    }

    const container = document.getElementById('skill-content');

    let tabContent;
    switch (currentTab) {
        case 'active':
            tabContent = renderActiveTab();
            break;
        case 'tier1':
            tabContent = renderTierTab(1);
            break;
        case 'tier2':
            tabContent = renderTierTab(2);
            break;
        case 'tier3':
            tabContent = renderTierTab(3);
            break;
        case 'tier4':
            tabContent = renderTierTab(4);
            break;
        case 'tier5':
            tabContent = renderTierTab(5);
            break;
        default:
            tabContent = renderTierTab(1);
    }

    // Track level for live updates
    const skill = gameState.getSkill(SKILLS.BOUNTY);
    lastKnownLevel = skill.level;

    // Show active contracts summary on tier tabs (not on active tab)
    const showSummary = currentTab !== 'active' && bountySkill.getActiveContracts().length > 0;

    container.innerHTML = `
        <div class="bounty-interface">
            ${renderCompactHeader()}

            <div class="bounty-content">
                ${renderTabs()}
                ${showSummary ? renderActiveContractsSummary() : ''}

                <div class="tab-content">
                    ${tabContent}
                </div>
            </div>
        </div>
    `;
}

/**
 * Update UI each tick
 */
export function updateBountyUI() {
    // Check if level changed (for tier unlock updates)
    const skill = gameState.getSkill(SKILLS.BOUNTY);
    if (skill.level !== lastKnownLevel) {
        lastKnownLevel = skill.level;
        renderBountyUI();
        return;
    }

    // Update compact header
    updateCompactHeader();

    // Update progress for active contracts (on active tab or summary)
    const activeContracts = bountySkill.getActiveContracts();
    for (const active of activeContracts) {
        const percent = bountySkill.getContractCompletionPercent(active.contractId);
        const card = document.querySelector(`[data-contract="${active.contractId}"]`);
        if (card) {
            // Update overall progress bar
            const progressFill = card.querySelector('.contract-progress-fill');
            const progressText = card.querySelector('.contract-progress-text');
            if (progressFill) progressFill.style.width = `${percent}%`;
            if (progressText) progressText.textContent = `${Math.floor(percent)}% Complete`;

            // Update individual requirement progress using data-req-id for reliable matching
            if (active.requirements) {
                for (const req of active.requirements) {
                    const reqId = req.itemId || req.mobId;
                    const progress = bountySkill.getRequirementProgress(active.contractId, reqId);
                    const reqEl = card.querySelector(`.contract-requirement[data-req-id="${reqId}"]`);
                    if (reqEl) {
                        const progressEl = reqEl.querySelector('.req-progress');
                        if (progressEl) {
                            // Cap displayed progress at target (no need to show 113/39)
                            const displayCurrent = Math.min(progress.current, progress.target);
                            progressEl.textContent = `${formatNumber(displayCurrent)} / ${formatNumber(progress.target)}`;
                        }
                        // Update complete class
                        if (progress.current >= progress.target) {
                            reqEl.classList.add('complete');
                        } else {
                            reqEl.classList.remove('complete');
                        }
                    }
                }
            }
        }

        // Also update mini summary progress bars
        const miniCards = document.querySelectorAll('.active-contract-mini');
        miniCards.forEach(miniContainer => {
            const miniName = miniContainer.querySelector('.mini-name');
            const contract = bountySkill.getContractDefinition(active.contractId);
            if (miniName && contract && miniName.textContent === contract.name) {
                const miniFill = miniContainer.querySelector('.mini-progress-fill');
                const miniPercentEl = miniContainer.querySelector('.mini-percent');
                if (miniFill) miniFill.style.width = `${percent}%`;
                if (miniPercentEl) miniPercentEl.textContent = `${Math.floor(percent)}%`;
            }
        });
    }
}

/**
 * Update compact header stats without full re-render
 */
function updateCompactHeader() {
    const skill = gameState.getSkill(SKILLS.BOUNTY);
    const xpPerHour = getXPPerHour(SKILLS.BOUNTY);
    const activeContracts = bountySkill.getActiveContracts();

    // Update XP values
    const xpTotalEl = document.querySelector('.bounty-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.bounty-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    // Update active contracts count
    const contractsCountEl = document.querySelector('.bounty-interface .contracts-count');
    if (contractsCountEl) contractsCountEl.textContent = `${activeContracts.length}/${BOUNTY_CONFIG.MAX_ACTIVE_CONTRACTS}`;

    // Update level display
    const isMax = skill.level >= GAME_CONFIG.MAX_LEVEL;
    const virtualEnabled = gameState.settings.virtualLevelsEnabled;
    const isVirtualMax = skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;

    let levelDisplay, progressPercent, progressText;

    if (isMax && virtualEnabled) {
        levelDisplay = `${skill.level} (${skill.virtualLevel})`;
        progressPercent = isVirtualMax ? 100 : (skill.currentXp / skill.nextLevelXp * 100);
        progressText = isVirtualMax ? 'Virtual Max' : `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    } else if (isMax) {
        levelDisplay = `${skill.level} (Max)`;
        progressPercent = 100;
        progressText = 'Mastered';
    } else {
        levelDisplay = skill.level;
        progressPercent = (skill.currentXp / skill.nextLevelXp * 100);
        progressText = `${formatWithCommas(skill.currentXp)} / ${formatWithCommas(skill.nextLevelXp)}`;
    }

    const levelEl = document.querySelector('.bounty-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    // Update progress bar
    const progressFill = document.querySelector('.bounty-interface .compact-progress-fill');
    if (progressFill) progressFill.style.width = `${progressPercent}%`;

    const progressPercentEl = document.querySelector('.bounty-interface .compact-progress-percent');
    if (progressPercentEl) progressPercentEl.textContent = `${Math.floor(progressPercent)}%`;

    const progressTextEl = document.querySelector('.bounty-interface .compact-progress-text');
    if (progressTextEl) progressTextEl.textContent = `${progressText} XP`;
}

/**
 * Initialize bounty UI
 */
export function initBountyUI() {
    // Clean up any orphaned contracts from previous updates
    bountySkill.cleanupOrphanedContracts();

    // Setup callbacks
    bountySkill.onContractAccepted = (contract) => {
        if (getCurrentSkillId() === SKILLS.BOUNTY && !isTalentTreeOpen()) {
            renderBountyUI();
        }
    };

    bountySkill.onContractDelivered = (contract, rewards) => {
        if (rewards.xpResult?.leveledUp) {
            toastSuccess(`Bounty level ${rewards.xpResult.newLevel}!`);
        }

        if (getCurrentSkillId() === SKILLS.BOUNTY && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderBountyUI();
            }
        }
    };

    bountySkill.onContractAbandoned = (contract) => {
        if (getCurrentSkillId() === SKILLS.BOUNTY && !isTalentTreeOpen()) {
            renderBountyUI();
        }
    };
}

/**
 * Switch between tabs
 */
export function switchTab(tab) {
    currentTab = tab;
    renderBountyUI();
}

/**
 * Accept a contract
 * @param {string} contractId - Contract ID
 * @param {boolean} isMega - Accept as mega contract
 */
export function acceptContract(contractId, isMega = false) {
    const contract = bountySkill.getOrGenerateContract(contractId.toUpperCase());
    const tierCost = CONTRACT_TIERS[contract?.tier]?.cost || 0;
    const actualCost = isMega ? tierCost * 5 : tierCost;
    const result = bountySkill.acceptContract(contractId, isMega);
    if (!result.success) {
        toastWarning(result.reason);
    } else {
        const costMsg = actualCost > 0 ? ` (-${formatNumber(actualCost)} gold)` : '';
        const megaMsg = isMega ? ' [MEGA]' : '';
        toastSuccess(`Contract accepted: ${result.contract.name}${megaMsg}${costMsg}`);
    }
    renderBountyUI();
}

/**
 * Abandon an active contract
 */
export function abandonContract(contractId) {
    const result = bountySkill.abandonContract(contractId);
    if (!result.success) {
        toastWarning(result.reason);
    }
    renderBountyUI();
}

/**
 * Deliver a completed contract
 */
export function deliverContract(contractId) {
    const result = bountySkill.deliverContract(contractId);
    if (!result.success) {
        toastWarning(result.reason);
    }
    // Floating notifications handle the XP/gold popups, no toast needed
    // Only re-render if we're on the bounty page (don't navigate away from other skills) and talent tree is not open
    if (getCurrentSkillId() === SKILLS.BOUNTY && !isTalentTreeOpen()) {
        renderBountyUI();
    }
}

/**
 * Pin a contract (Dedicated Supplier talent)
 */
export function pinContract(contractId) {
    const result = bountySkill.pinContract(contractId);
    if (!result.success) {
        toastWarning(result.reason);
    } else {
        toastSuccess(`Pinned: ${result.contract.name}`);
    }
    renderBountyUI();
}

/**
 * Unpin the current pinned contract
 */
export function unpinContract() {
    bountySkill.unpinContract();
    toastSuccess('Contract unpinned');
    renderBountyUI();
}

// Export for window attachment
if (typeof window !== 'undefined') {
    window.bountyUI = {
        switchTab,
        acceptContract,
        abandonContract,
        deliverContract,
        pinContract,
        unpinContract
    };
}

export default {
    renderBountyUI,
    updateBountyUI,
    initBountyUI,
    switchTab,
    acceptContract,
    abandonContract,
    deliverContract,
    pinContract,
    unpinContract
};
